import {
  CreateProposalAttachmentsDto, CreateProposalItemsDto, CreateProposalPricingSummaryDto, CreateProposalsDto,
  ProposalsEntity, UpdateProposalAttachmentsDto, UpdateProposalItemsDto, UpdateProposalPricingSummaryDto, UpdateProposalsDto,
  ProposalAttachmentsEntity, ProposalItemsEntity, ProposalPricingSummaryEntity
} from '@crm/dto';
import { ApiResponseInterface, ApiResponseStatus, ProposalItemStatus, ProposalStatus, UserRole } from '@crm/interface';
import { API_RESPONSE, prepareUpdateFields } from '@crm/utils';
import { Injectable, Logger } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { In, Repository } from 'typeorm';

const PROPOSAL_APPROVAL_LIMIT = 250000;

@Injectable()
export class ProposalsService {
  private readonly logger = new Logger(ProposalsService.name);
    
  constructor(
    @InjectRepository(ProposalsEntity)
    private readonly proposalsRepo: Repository<ProposalsEntity>,

    @InjectRepository(ProposalAttachmentsEntity)
    private readonly attachmentsRepo: Repository<ProposalAttachmentsEntity>,

    @InjectRepository(ProposalItemsEntity)
    private readonly itemsRepo: Repository<ProposalItemsEntity>,

    @InjectRepository(ProposalPricingSummaryEntity)
    private readonly summaryRepo: Repository<ProposalPricingSummaryEntity>,
  ) {}

  // ====== PROPOSALS ======
  async getAll(): Promise<ApiResponseInterface> {
    try {
      const proposals = await this.proposalsRepo.find({
        relations: ['created_by', 'updated_by', 'proposal_attachments', 'proposal_items', 'proposal_pricing_summary'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSALS.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSALS.GET_ALL.MESSAGE,
        data: proposals,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSALS.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.PROPOSALS.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // Get proposals for supervisor (created by managers and supervisors)
  async getForSupervisor(): Promise<ApiResponseInterface> {
    try {
      const proposals = await this.proposalsRepo.find({
        where: { created_by: { role: In([UserRole.MANAGER, UserRole.SUPERVISOR]) } },
        relations: ['created_by', 'updated_by', 'company_ref', 'proposal_attachments', 'proposal_items', 'proposal_pricing_summary'],
      });
      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSALS.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSALS.GET_ALL.MESSAGE,
        data: proposals,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSALS.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.PROPOSALS.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // Approve proposal — management can approve all, supervisor cannot approve >250k
  async approveProposal(id: string, status: ProposalStatus, userRole: UserRole, updatedById?: string): Promise<ApiResponseInterface> {
    try {
      const proposal = await this.proposalsRepo.findOne({
        where: { id },
        relations: ['proposal_pricing_summary'],
      });

      if (!proposal) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.PROPOSALS.UPDATE.CODE,
          message: 'Proposal not found.',
          data: null,
        };
      }

      const grandTotal = proposal.proposal_pricing_summary?.grand_total ?? 0;

      if (userRole === UserRole.SUPERVISOR && Number(grandTotal) > PROPOSAL_APPROVAL_LIMIT) {
        return {
          status: ApiResponseStatus.ERROR,
          code: 403,
          message: `Supervisors cannot approve proposals exceeding ₱${PROPOSAL_APPROVAL_LIMIT.toLocaleString()}. Management approval required.`,
          data: null,
        };
      }

      proposal.status = status;
      if (updatedById) proposal.updated_by = { id: updatedById } as any;
      await this.proposalsRepo.save(proposal);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSALS.UPDATE.CODE,
        message: 'Proposal status updated successfully.',
        data: proposal,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSALS.UPDATE.CODE,
        message: API_RESPONSE.ERROR.PROPOSALS.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  async getByUserId(userId: string): Promise<ApiResponseInterface> {
    try {
      const proposals = await this.proposalsRepo.find({
        where: { created_by: { id: userId } },
        relations: ['created_by', 'updated_by', 'proposal_attachments', 'proposal_items', 'proposal_pricing_summary'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSALS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSALS.GET_BY_ID.MESSAGE,
        data: proposals,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSALS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.PROPOSALS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  async getById(id: string): Promise<ApiResponseInterface> {
    try {
      const proposal = await this.proposalsRepo.findOne({
        where: { id },
        relations: ['created_by', 'updated_by', 'proposal_attachments', 'proposal_items', 'proposal_pricing_summary'],
      });

      if (!proposal) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.PROPOSALS.GET_BY_ID.CODE,
          message: API_RESPONSE.ERROR.PROPOSALS.GET_BY_ID.MESSAGE,
          data: null,
        };
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSALS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSALS.GET_BY_ID.MESSAGE,
        data: proposal,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSALS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.PROPOSALS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  async create(dto: CreateProposalsDto): Promise<ApiResponseInterface> {
    try {
      const newProposal = this.proposalsRepo.create(dto);
      await this.proposalsRepo.save(newProposal);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSALS.CREATE.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSALS.CREATE.MESSAGE,
        data: newProposal,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSALS.CREATE.CODE,
        message: API_RESPONSE.ERROR.PROPOSALS.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  async update(dto: UpdateProposalsDto): Promise<ApiResponseInterface> {
    try {
      const proposal = await this.proposalsRepo.findOne({ where: { id: dto.id } });

      if (!proposal) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.PROPOSALS.UPDATE.CODE,
          message: API_RESPONSE.ERROR.PROPOSALS.UPDATE.MESSAGE,
          data: null,
        };
      }

      const updateData = prepareUpdateFields(dto, { updated_by_id: 'updated_by' });
      Object.assign(proposal, updateData);
      await this.proposalsRepo.save(proposal);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSALS.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSALS.UPDATE.MESSAGE,
        data: proposal,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSALS.UPDATE.CODE,
        message: API_RESPONSE.ERROR.PROPOSALS.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  async softDelete(dto: UpdateProposalsDto): Promise<ApiResponseInterface> {
    try {
      const proposal = await this.proposalsRepo.findOne({ where: { id: dto.id } });
      if (!proposal) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.PROPOSALS.DELETE.CODE,
          message: API_RESPONSE.ERROR.PROPOSALS.DELETE.MESSAGE,
          data: null,
        };
      }

      proposal.status = ProposalStatus.DELETED;
      await this.proposalsRepo.save(proposal);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSALS.DELETE.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSALS.DELETE.MESSAGE,
        data: proposal,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSALS.DELETE.CODE,
        message: API_RESPONSE.ERROR.PROPOSALS.DELETE.MESSAGE,
        data: error,
      };
    }
  }

  async saveAsDraft(dto: UpdateProposalsDto): Promise<ApiResponseInterface> {
    try {
      const proposal = await this.proposalsRepo.findOne({ where: { id: dto.id } });
      if (!proposal) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.PROPOSALS.DELETE.CODE,
          message: API_RESPONSE.ERROR.PROPOSALS.DELETE.MESSAGE,
          data: null,
        };
      }

      proposal.status = ProposalStatus.DRAFT;
      await this.proposalsRepo.save(proposal);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSALS.DELETE.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSALS.DELETE.MESSAGE,
        data: proposal,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSALS.DELETE.CODE,
        message: API_RESPONSE.ERROR.PROPOSALS.DELETE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== PROPOSAL ATTACHMENTS ======
  async createProposalAttachment(dto: CreateProposalAttachmentsDto): Promise<ApiResponseInterface> {
    try {
      const newAttachment = this.attachmentsRepo.create(dto);
      await this.attachmentsRepo.save(newAttachment);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSAL_ATTACHMENT.CREATE.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSAL_ATTACHMENT.CREATE.MESSAGE,
        data: newAttachment,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSAL_ATTACHMENT.CREATE.CODE,
        message: API_RESPONSE.ERROR.PROPOSAL_ATTACHMENT.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  async updateProposalAttachment(dto: UpdateProposalAttachmentsDto): Promise<ApiResponseInterface> {
    try {
      const attachment = await this.attachmentsRepo.findOne({
        where: {
          proposal: { id: dto.proposal_id },
          attachment: { id: dto.attachment_id },
        },
        relations: ['proposal', 'attachment'],
      });
      if (!attachment) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.PROPOSAL_ATTACHMENT.UPDATE.CODE,
          message: API_RESPONSE.ERROR.PROPOSAL_ATTACHMENT.UPDATE.MESSAGE,
          data: null,
        };
      }

      Object.assign(attachment, dto);
      await this.attachmentsRepo.save(attachment);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSAL_ATTACHMENT.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSAL_ATTACHMENT.UPDATE.MESSAGE,
        data: attachment,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSAL_ATTACHMENT.UPDATE.CODE,
        message: API_RESPONSE.ERROR.PROPOSAL_ATTACHMENT.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== PROPOSAL ITEMS ======
  async getProposalItemByProposalId(id: string): Promise<ApiResponseInterface> {
    try {
      const items = await this.itemsRepo.find({
        where: { proposal: { id } },
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSAL_ITEMS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSAL_ITEMS.GET_BY_ID.MESSAGE,
        data: items,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSAL_ITEMS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.PROPOSAL_ITEMS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  async createProposalItem(dto: CreateProposalItemsDto): Promise<ApiResponseInterface> {
    try {
      const createData: any = { ...dto };
      if (dto.attachment_item_img) {
        createData.attachment_item_img = { id: dto.attachment_item_img };
      }

      // Map proposal_id to proposal relation
      if (dto.proposal_id) {
        createData.proposal = { id: dto.proposal_id };
        delete createData.proposal_id;
      }

      const newItem = this.itemsRepo.create(createData);
      await this.itemsRepo.save(newItem);
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSAL_ITEMS.CREATE.CODE,
        message: API_RESPONSE.ERROR.PROPOSAL_ITEMS.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  async updateProposalItem(dto: UpdateProposalItemsDto): Promise<ApiResponseInterface> {
    try {
      const item = await this.itemsRepo.findOne({ where: { id: dto.id } });
      if (!item) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.PROPOSAL_ITEMS.UPDATE.CODE,
          message: API_RESPONSE.ERROR.PROPOSAL_ITEMS.UPDATE.MESSAGE,
          data: null,
        };
      }

      Object.assign(item, dto);
      await this.itemsRepo.save(item);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSAL_ITEMS.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSAL_ITEMS.UPDATE.MESSAGE,
        data: item,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSAL_ITEMS.UPDATE.CODE,
        message: API_RESPONSE.ERROR.PROPOSAL_ITEMS.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  async softDeleteProposalItem(dto: UpdateProposalItemsDto): Promise<ApiResponseInterface> {
    try {
      const item = await this.itemsRepo.findOne({ where: { id: dto.id } });
      if (!item) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.PROPOSAL_ITEMS.DELETE.CODE,
          message: API_RESPONSE.ERROR.PROPOSAL_ITEMS.DELETE.MESSAGE,
          data: null,
        };
      }

      item.status = ProposalItemStatus.DELETED;
      await this.itemsRepo.save(item);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSAL_ITEMS.DELETE.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSAL_ITEMS.DELETE.MESSAGE,
        data: item,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSAL_ITEMS.DELETE.CODE,
        message: API_RESPONSE.ERROR.PROPOSAL_ITEMS.DELETE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== PROPOSAL PRICING SUMMARY ======
  async getProposalSummaryByProposalId(id: string): Promise<ApiResponseInterface> {
    try {
      const summary = await this.summaryRepo.findOne({ where: { proposal: { id } } });
      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSAL_PRICING_SUMMARY.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSAL_PRICING_SUMMARY.GET_BY_ID.MESSAGE,
        data: summary,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSAL_PRICING_SUMMARY.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.PROPOSAL_PRICING_SUMMARY.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  async createProposalSummary(dto: CreateProposalPricingSummaryDto): Promise<ApiResponseInterface> {
    try {
      const newSummary = this.summaryRepo.create(dto);
      await this.summaryRepo.save(newSummary);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSAL_PRICING_SUMMARY.CREATE.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSAL_PRICING_SUMMARY.CREATE.MESSAGE,
        data: newSummary,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSAL_PRICING_SUMMARY.CREATE.CODE,
        message: API_RESPONSE.ERROR.PROPOSAL_PRICING_SUMMARY.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  async updateProposalSummary(dto: UpdateProposalPricingSummaryDto): Promise<ApiResponseInterface> {
    try {
      const summary = await this.summaryRepo.findOne({ where: { id: dto.id } });
      if (!summary) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.PROPOSAL_PRICING_SUMMARY.UPDATE.CODE,
          message: API_RESPONSE.ERROR.PROPOSAL_PRICING_SUMMARY.UPDATE.MESSAGE,
          data: null,
        };
      }

      Object.assign(summary, dto);
      await this.summaryRepo.save(summary);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.PROPOSAL_PRICING_SUMMARY.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.PROPOSAL_PRICING_SUMMARY.UPDATE.MESSAGE,
        data: summary,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.PROPOSAL_PRICING_SUMMARY.UPDATE.CODE,
        message: API_RESPONSE.ERROR.PROPOSAL_PRICING_SUMMARY.UPDATE.MESSAGE,
        data: error,
      };
    }
  }
}